import { Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { PER_ME_ENABLED } from "../lib/perMe";
import { ShoppingBag, User, UserPlus, Store, Menu, X, LogOut, LayoutDashboard, Package, Phone, MessageCircle, Shield, Heart, Facebook, Instagram } from "lucide-react";
import { PaymentBadges } from "./PaymentBadges";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useState, useEffect, useRef, Suspense } from "react";
import { CookieBanner } from "./CookieBanner";
import { FloraChat } from "./FloraChat";
import { TrackingScripts } from "./TrackingScripts";
import { BackButton } from "./BackButton";

export default function Layout() {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartBounce, setCartBounce] = useState(false);
  const prevCartCount = useRef(cartCount);

  useEffect(() => {
    if (cartCount > prevCartCount.current) {
      setCartBounce(true);
      const timer = setTimeout(() => setCartBounce(false), 600);
      return () => clearTimeout(timer);
    }
    prevCartCount.current = cartCount;
  }, [cartCount]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-cream-dark" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src="/logo-full.png" alt="Flowerlive" className="h-9" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link 
                to="/shops" 
                className="text-sage hover:text-forest transition-colors font-medium"
                data-testid="nav-shops"
              >
                Invia Fiori
              </Link>
              {PER_ME_ENABLED && <Link 
                to="/fiori-per-me" 
                className="text-sage hover:text-forest transition-colors font-medium"
                data-testid="nav-per-me"
              >
                Fiori per me
              </Link>}
              <Link 
                to="/blog" 
                className="text-sage hover:text-forest transition-colors font-medium"
                data-testid="nav-blog"
              >
                Blog
              </Link>
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-4">
              {/* Cart */}
              <Link to="/cart" className={`relative transition-transform duration-300 ${cartBounce ? "scale-125" : ""}`} data-testid="cart-link">
                <ShoppingBag className="h-6 w-6 text-forest" />
                {cartCount > 0 && (
                  <span className={`absolute -top-2 -right-2 bg-brand-pink text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium transition-transform duration-300 ${cartBounce ? "scale-125 animate-pulse" : ""}`}>
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* User Menu */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid="user-menu-trigger">
                      <User className="h-6 w-6 text-forest" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-forest">{user.name}</p>
                      <p className="text-xs text-sage-light">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    {user.role === "admin" && (
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-link">
                        <Shield className="mr-2 h-4 w-4" />
                        Supervisione Flowerlive
                      </DropdownMenuItem>
                    )}
                    {user.role === "florist" ? (
                      <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="dashboard-link">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Dashboard
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem onClick={() => navigate("/my-orders")} data-testid="my-orders-link">
                      <Package className="mr-2 h-4 w-4" />
                      I Miei Ordini
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/ricorrenze")} data-testid="my-dates-link">
                      <Heart className="mr-2 h-4 w-4" />
                      Le Mie Ricorrenze
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-button">
                      <LogOut className="mr-2 h-4 w-4" />
                      Esci
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="hidden sm:flex items-center gap-2">
                  <Button variant="ghost" onClick={() => navigate("/login")} data-testid="login-button">
                    Accedi
                  </Button>
                  <Button 
                    className="bg-brand text-white hover:bg-brand-dark"
                    onClick={() => navigate("/register")}
                    data-testid="register-button"
                  >
                    Registrati
                  </Button>
                </div>
              )}

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? "Chiudi menu" : "Apri menu"}
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-cream-dark bg-white" data-testid="mobile-menu">
            <nav className="px-4 py-2 divide-y divide-cream-dark/70">
              {[
                { to: "/shops", label: "Invia Fiori", icon: ShoppingBag },
                ...(PER_ME_ENABLED ? [{ to: "/fiori-per-me", label: "Fiori per me", icon: Heart, testid: "nav-per-me-mobile" }] : []),
                ...(!user ? [
                  { to: "/login", label: "Accedi", icon: User },
                  { to: "/register", label: "Registrati", icon: UserPlus },
                  { to: "/fioristi", label: "Sei un fiorista? Apri il tuo negozio", icon: Store, accent: true },
                ] : [
                  ...(user.role === "admin" ? [{ to: "/admin", label: "Supervisione Flowerlive", icon: Shield }] : []),
                  ...(user.role === "florist" ? [{ to: "/dashboard", label: "La mia dashboard", icon: LayoutDashboard }, { to: "/dashboard/orders", label: "Ordini ricevuti", icon: Package }] : []),
                  { to: "/my-orders", label: "I miei ordini", icon: Package },
                  { to: "/ricorrenze", label: "Le mie ricorrenze", icon: Heart },
                ]),
              ].map(item => (
                <Link key={item.to} to={item.to} onClick={() => setMobileMenuOpen(false)} data-testid={item.testid}
                  className={`flex items-center gap-3 py-3.5 min-h-[48px] font-medium transition-colors ${item.accent ? "text-brand-pink" : "text-forest hover:text-brand"}`}>
                  <item.icon className="h-5 w-5 shrink-0 opacity-70" />{item.label}
                </Link>
              ))}
              {user && (
                <button onClick={() => { setMobileMenuOpen(false); handleLogout(); }} className="flex w-full items-center gap-3 py-3.5 min-h-[48px] font-medium text-red-600" data-testid="mobile-logout">
                  <LogOut className="h-5 w-5 shrink-0 opacity-70" />Esci
                </button>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>
        <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-forest" /></div>}>
          <Outlet />
        </Suspense>
      </main>
      <CookieBanner />
      <TrackingScripts />
      <BackButton />
      <FloraChat />

      {/* Footer */}
      <footer className="bg-forest text-white mt-20" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="mb-5">
                <img src="/logo-icon.png" alt="Flowerlive" className="h-16 w-16" />
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                Il marketplace dei fioristi italiani. Trova il fiorista perfetto per ogni occasione.
              </p>
              <div className="flex items-center gap-3 mt-5" data-testid="footer-social">
                <a href="https://www.facebook.com/Flowerliveshop" target="_blank" rel="noopener noreferrer" aria-label="Flowerlive su Facebook" className="h-10 w-10 rounded-full bg-[#1877F2] hover:brightness-110 flex items-center justify-center transition-[filter,transform] hover:-translate-y-0.5" data-testid="footer-facebook">
                  <Facebook className="h-5 w-5 text-white" fill="currentColor" />
                </a>
                <a href="https://www.instagram.com/flowerlive_italia" target="_blank" rel="noopener noreferrer" aria-label="Flowerlive su Instagram" className="h-10 w-10 rounded-full bg-[radial-gradient(circle_at_30%_107%,#fdf497_0%,#fdf497_5%,#fd5949_45%,#d6249f_60%,#285AEB_90%)] hover:brightness-110 flex items-center justify-center transition-[filter,transform] hover:-translate-y-0.5" data-testid="footer-instagram">
                  <Instagram className="h-5 w-5 text-white" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Link Utili</h4>
              <ul className="space-y-2 text-sm text-white/70">
                <li><Link to="/shops" className="hover:text-white transition-colors">Invia Fiori</Link></li>
                {PER_ME_ENABLED && <li><Link to="/fiori-per-me" className="hover:text-white transition-colors">Fiori per me</Link></li>}
                <li><Link to="/catalogo" className="hover:text-white transition-colors" data-testid="footer-catalog-link">Catalogo completo</Link></li>
                <li><Link to="/apri-negozio" className="hover:text-white transition-colors">Diventa Fiorista</Link></li>
                <li>
                  <Link to="/privacy/privacy_policy" className="hover:text-white transition-colors flex items-center gap-1.5" data-testid="privacy-policy-link">
                    <Shield className="h-3.5 w-3.5" />
                    Privacy Policy
                  </Link>
                </li>
                <li><Link to="/privacy/cookie_policy" className="hover:text-white transition-colors" data-testid="cookie-policy-link">Cookie Policy</Link></li>
                <li><button type="button" onClick={() => window.dispatchEvent(new Event("fl:open-cookie-banner"))} className="hover:text-white transition-colors" data-testid="cookie-preferences-btn">Preferenze cookie</button></li>
                <li><Link to="/privacy/documentazione" className="hover:text-white transition-colors" data-testid="legal-docs-link">Documentazione legale</Link></li>
                <li><a href="mailto:info@flowerlive.it" className="hover:text-white transition-colors" data-testid="footer-contact-email">info@flowerlive.it</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Cosa cerchi</h4>
              <ul className="space-y-2 text-sm text-white/70" data-testid="footer-seo-links">
                <li><Link to="/fiori-a-domicilio" className="hover:text-white transition-colors">Fiori a domicilio</Link></li>
                <li><Link to="/fiorista-vicino-a-me" className="hover:text-white transition-colors">Fiorista vicino a me</Link></li>
                <li><Link to="/bouquet-di-fiori" className="hover:text-white transition-colors">Bouquet di fiori</Link></li>
                <li><Link to="/piante-da-interno" className="hover:text-white transition-colors">Piante da interno</Link></li>
                <li><Link to="/bouquet-sposa" className="hover:text-white transition-colors">Bouquet sposa</Link></li>
                <li><Link to="/fiori-per-laurea" className="hover:text-white transition-colors">Fiori per laurea</Link></li>
                <li><Link to="/rosa-stabilizzata" className="hover:text-white transition-colors">Rosa stabilizzata</Link></li>
                <li><Link to="/piattaforma-per-fioristi" className="hover:text-white transition-colors">Piattaforma per fioristi</Link></li>
                <li><Link to="/marketing-per-fioristi" className="hover:text-white transition-colors">Marketing per fioristi</Link></li>
                <li><Link to="/mappa-del-sito" className="hover:text-white transition-colors" data-testid="footer-sitemap-link">Mappa del sito</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contatti</h4>
              <ul className="space-y-3 text-sm text-white/70">
                <li>
                  <a href="https://wa.me/393399951090" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-brand-light transition-colors" data-testid="whatsapp-link">
                    <MessageCircle className="h-4 w-4 text-brand-light" />
                    <span>WhatsApp: 339 995 1090</span>
                  </a>
                </li>
                <li>
                  <a href="tel:800940365" className="flex items-center gap-2 hover:text-brand-light transition-colors" data-testid="tollfree-link">
                    <Phone className="h-4 w-4 text-brand-light" />
                    <span>Numero Verde: 800 940 365</span>
                  </a>
                </li>
                <li className="text-white/50">info@flowerlive.it</li>
              </ul>
              <div className="mt-6"><PaymentBadges /></div>
            </div>
          </div>
          <div className="border-t border-white/20 mt-8 pt-8 text-center text-sm text-white/50">
            <p>&copy; 2026 Flowerlive. Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
